import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Trophy, Medal, Search, ArrowRight, Crown, Star, Sparkles, Zap, Globe, ExternalLink, Loader2, Filter } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getScoreColor(score) {
  if (score >= 75) return "#22c55e";
  if (score >= 50) return "#3b82f6";
  if (score >= 25) return "#f59e0b";
  return "#ef4444";
}

const rankIcons = {
  0: <Crown className="w-5 h-5 text-yellow-400" />,
  1: <Medal className="w-5 h-5 text-zinc-300" />,
  2: <Medal className="w-5 h-5 text-orange-600" />,
};

const rankBg = {
  0: "bg-yellow-400/5 border-yellow-400/30",
  1: "bg-zinc-300/5 border-zinc-400/20",
  2: "bg-orange-600/5 border-orange-600/20",
};

export default function LeaderboardPage() {
  const [entries, setEntries] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, [selectedIndustry]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const params = selectedIndustry !== "All" ? { industry: selectedIndustry } : {};
      const res = await axios.get(`${API}/leaderboard`, { params });
      setEntries(res.data.entries);
      if (res.data.industries?.length) setIndustries(res.data.industries);
    } catch (e) {
      console.error("Failed to fetch leaderboard:", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative" data-testid="leaderboard-page">
      {/* Noise */}
      <div className="fixed inset-0 opacity-[0.03] pointer-events-none z-50"
        style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")" }} />

      {/* Top glow */}
      <div className="absolute top-[-150px] left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-yellow-500/5 blur-[200px] pointer-events-none" />

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <a href="https://geokath.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:opacity-90 transition-opacity">
          <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center font-black text-white text-lg" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>G</div>
          <div>
            <span className="text-white font-bold text-lg tracking-tight block leading-none" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Geokath Marketing</span>
            <span className="text-[10px] text-zinc-500 tracking-wider">AI Search Audit</span>
          </div>
        </a>
        <a href="/" className="text-sm text-orange-500 hover:text-orange-400 transition-colors flex items-center gap-1" data-testid="run-audit-link">
          Run Free Audit <ArrowRight className="w-4 h-4" />
        </a>
      </header>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 py-8 md:py-16">
        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-yellow-400/10 border border-yellow-400/20 rounded-full px-4 py-1.5 mb-6">
            <Trophy className="w-4 h-4 text-yellow-400" />
            <span className="text-xs font-bold text-yellow-400 uppercase tracking-wider">Visibility Leaderboard</span>
          </div>

          <h1 className="text-4xl md:text-6xl font-black tracking-tighter mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} data-testid="leaderboard-heading">
            Most <span className="text-orange-500">Visible</span> Businesses
          </h1>
          <p className="text-zinc-400 max-w-lg mx-auto text-base mb-8">
            These businesses lead in AI search visibility. Run your free audit to see where you rank — and claim your spot on the board.
          </p>

          <a href="/"
            className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold py-3 px-6 rounded-md transition-all duration-300 hover:bg-orange-600 shadow-[0_0_25px_rgba(249,115,22,0.3)] hover:shadow-[0_0_40px_rgba(249,115,22,0.5)]"
            data-testid="leaderboard-cta">
            Check Your Score <ArrowRight className="w-4 h-4" />
          </a>
        </motion.div>

        {/* Filter */}
        {industries.length > 1 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="flex items-center gap-2 mb-8 overflow-x-auto pb-2" data-testid="industry-filter">
            <Filter className="w-4 h-4 text-zinc-500 flex-shrink-0" />
            <button onClick={() => setSelectedIndustry("All")}
              className={`text-xs px-3 py-1.5 rounded-full font-semibold transition-colors whitespace-nowrap ${selectedIndustry === "All" ? "bg-orange-500 text-white" : "bg-zinc-800 text-zinc-400 hover:text-white"}`}>
              All Industries
            </button>
            {industries.map(ind => (
              <button key={ind} onClick={() => setSelectedIndustry(ind)}
                className={`text-xs px-3 py-1.5 rounded-full font-semibold transition-colors whitespace-nowrap ${selectedIndustry === ind ? "bg-orange-500 text-white" : "bg-zinc-800 text-zinc-400 hover:text-white"}`}>
                {ind}
              </button>
            ))}
          </motion.div>
        )}

        {/* Leaderboard */}
        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="leaderboard-loading">
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
          </div>
        ) : entries.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="text-center py-20 border border-zinc-800 rounded-xl bg-[#1a1a1c]/30" data-testid="leaderboard-empty">
            <Trophy className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-zinc-400 mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>No entries yet</h3>
            <p className="text-sm text-zinc-600 mb-6">Be the first to claim your spot on the leaderboard!</p>
            <a href="/" className="inline-flex items-center gap-2 text-orange-500 font-semibold hover:text-orange-400 transition-colors">
              Run Your Audit <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        ) : (
          <div className="space-y-3" data-testid="leaderboard-list">
            {entries.map((entry, i) => (
              <motion.div key={entry.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className={`border rounded-xl p-5 transition-all duration-200 hover:-translate-y-0.5 ${i < 3 ? rankBg[i] : "border-zinc-800 bg-[#1a1a1c]/30"}`}
                data-testid={`leaderboard-entry-${i}`}
              >
                <div className="flex items-center gap-4">
                  {/* Rank */}
                  <div className="w-10 h-10 rounded-lg bg-[#0f0f10] flex items-center justify-center flex-shrink-0">
                    {i < 3 ? rankIcons[i] : (
                      <span className="text-sm font-black text-zinc-500" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{i + 1}</span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-white truncate" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                        {entry.business_name}
                      </h3>
                      {i === 0 && <Star className="w-4 h-4 text-yellow-400 flex-shrink-0" />}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-zinc-500 mt-0.5">
                      <span>{entry.industry}</span>
                      <span className="text-zinc-700">|</span>
                      <span>{entry.city}</span>
                    </div>
                  </div>

                  {/* Scores */}
                  <div className="hidden md:flex items-center gap-6">
                    <div className="text-center">
                      <div className="text-xs text-zinc-600 mb-0.5">SEO</div>
                      <span className="text-sm font-bold" style={{ color: getScoreColor(entry.seo_score) }}>{entry.seo_score}%</span>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-zinc-600 mb-0.5">AI</div>
                      <span className="text-sm font-bold" style={{ color: getScoreColor(entry.ai_score) }}>{entry.ai_score}%</span>
                    </div>
                  </div>

                  {/* Overall */}
                  <div className="text-right flex-shrink-0">
                    <span className="text-2xl font-black" style={{ color: getScoreColor(entry.overall_score), fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                      {entry.overall_score}%
                    </span>
                    <div className="text-[10px] text-zinc-600 uppercase tracking-wider">Overall</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Bottom CTA */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
          className="mt-16 border border-orange-500/20 rounded-2xl p-8 md:p-12 text-center bg-orange-500/5">
          <Trophy className="w-8 h-8 text-orange-500 mx-auto mb-4" />
          <h3 className="text-2xl md:text-3xl font-black tracking-tighter mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Want to Be on This List?
          </h3>
          <p className="text-zinc-400 mb-8 max-w-md mx-auto">
            Run your free AI visibility audit, then opt-in to claim your spot. Or book a strategy call to boost your score.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/"
              className="inline-flex items-center justify-center gap-2 bg-orange-500 text-white font-bold py-3.5 px-8 rounded-md transition-all duration-300 hover:bg-orange-600 shadow-[0_0_25px_rgba(249,115,22,0.35)]"
              data-testid="bottom-cta-audit">
              Run Free Audit <ArrowRight className="w-5 h-5" />
            </a>
            <a href="https://geokath.com/contact" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 border border-zinc-700 text-white font-semibold py-3.5 px-8 rounded-md transition-all duration-300 hover:border-zinc-500"
              data-testid="bottom-cta-consult">
              Book Strategy Call <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </motion.div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <a href="https://geokath.com" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-zinc-600 hover:text-orange-500 transition-colors text-sm">
            <ExternalLink className="w-4 h-4" /> Powered by Geokath Marketing — Sacramento's SEO Experts Since 1996
          </a>
        </div>
      </div>
    </div>
  );
}
